/**
 * SimulationManager Connect-RPC Service
 * BFF 서버와의 Connect-RPC 통신을 담당
 *
 * 오케스트레이션: CreateSimulation → AddTask × N → Build (3단계 Builder)
 */

import { ConnectError, createPromiseClient } from "@connectrpc/connect";
import type { PromiseClient } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { startSessionStreamMux, stopSessionStreamMux } from '@/app/services/sse/sessionStreamMux';
import { reconcileRunAfterSessionLoss, reconcileRunWithSession } from '@/app/services/session/sessionReconcile';
import { waitForAuthRenewal } from '@/app/services/backend/authRenewal';
import { BFF_URL, createAuthInterceptor, getRuntimeEnvVar } from "@/app/services/connectCommon";
import { useSessionStore } from "@/app/stores/sessionStore";
import { SimulationManager } from "@/stubs/sm/simulation_manager_connect";
import {
  AddTaskRequest,
  BuildRequest,
  SessionId,
  StartMode,
} from "@/stubs/sm/simulation_manager_pb";
import type { SessionState, SimulationInfoList } from "@/stubs/sm/simulation_manager_pb";
import { Empty } from "@bufbuild/protobuf";
import { transportMeaningOf } from "@/shared/transportMeaning";

const TASK_TYPE = getRuntimeEnvVar(
  'VITE_TASK_TYPE',
  import.meta.env.VITE_TASK_TYPE,
  'mars'
);

const SESSION_FIRST_MESSAGE_TIMEOUT_MS = 45000;

/**
 * Auth + Session interceptor
 */
const authSessionInterceptor = createAuthInterceptor({
  getSessionId: () => useSessionStore.getState().sessionId,
});

const transport = createConnectTransport({
  baseUrl: `${BFF_URL}/api`,
  interceptors: [authSessionInterceptor],
});

const smClient: PromiseClient<typeof SimulationManager> = createPromiseClient(
  SimulationManager,
  transport
);

// ============================================
// Session Management
// ============================================

export function setSessionId(sessionId: string | null): void {
  useSessionStore.getState().setSessionId(sessionId);
  console.log(`[SimulationManagerService] Session ID set: ${sessionId}`);
}

export function getSessionId(): string | null {
  return useSessionStore.getState().getSessionId();
}

// liveness 절단 후의 재접속·백오프·세션 소실 판정은 탭 통합 스트림
// (sessionStreamMux)이 진다 — SM replica drain/failover 시 mux가 같은 세션에
// BFF 경유 AttachSession으로 재결합한다.

let _sessionAbortController: AbortController | null = null;
let _sessionIdPromise: Promise<string> | null = null;

/**
 * 세션 수명 시작 — id를 확보하고 liveness 홀드를 **탭 통합 스트림**(mux)에
 * 넘긴다 (streaming-decision D1/D2).
 *
 * - id가 없으면: CreateSession 스트림을 **발급용으로만 잠깐** 연다. 첫 프레임
 *   으로 id를 받으면 mux를 먼저 수립하고(선수립) 발급 스트림을 닫는다(후해제).
 * - id가 있으면(attach): mux가 BFF 경유로 AttachSession을 쥔다. 유휴 세션은
 *   보낼 프레임이 없어 스트림만으로 실재를 확인할 수 없으므로, GetSessionState
 *   unary 프로브로 실재를 확인한 뒤 settle한다(종전 attach 첫-에코 확인의 등가).
 * - 이후의 재접속·백오프·커서 재개는 전부 mux 소관. 서버가 세션을 모른다고
 *   확정되면(NOT_FOUND) 종전의 "재접속 창 소진"과 동일하게 세션을 접는다.
 */
async function runSessionLifecycle(
  abortController: AbortController,
  initialSessionId: string | null,
  onReady: (sessionId: string) => void,
  onFatal: (error: Error) => void,
): Promise<void> {
  const signal = abortController.signal;
  let knownId = initialSessionId;

  const startMux = (sessionId: string) => {
    startSessionStreamMux(
      sessionId,
      signal,
      (error) => {
        // 세션 소실은 **종결**이다 — mux 상태(세션·등록)까지 비우고 이 수명을
        // 닫아야, 이후의 등록 변경이 죽은 세션으로 재접속하거나 다음 세션이
        // 옛 sim 등록을 물려받지 않는다(#316 리뷰).
        console.error('[SimulationManagerService] 세션 소실(서버 미인지) — 세션을 접는다', error);
        stopSessionStreamMux();
        abortController.abort();
        setSessionId(null);
        // 서버가 세션을 모른다 = 그 세션에 실행이 없다 — 화면의 실행을 재접속 대조와 같은 규칙으로 기록과 맞춘다
        // (접기만 하면 공백 중 끝난 실행이 RUNNING 으로 남는다). 접은 수명의 신호는 이미 abort 됐으니 넘기지 않는다.
        void reconcileRunAfterSessionLoss(sessionId)
          .then((outcome) => {
            console.log(`[SimulationManagerService] 세션 소실 대조: ${outcome.kind}`);
          })
          .catch((err) => {
            console.error('[SimulationManagerService] 세션 소실 대조 실패:', err);
          });
      },
      // 끊긴 뒤의 재수립은 대조를 먼저 지난다 — 세션 응답(실재 프로브와 같은 호출)이 정본, 공백 중 끝난 실행은
      // 기록으로 닫는다(sessionReconcile). NotFound 는 mux 가 세션 소실로 받는다.
      async (id, sig) => {
        const state = await smClient.getSessionState(
          { sessionId: id },
          { signal: sig, headers: { 'x-session-id': id } },
        );
        const outcome = await reconcileRunWithSession(state, sig);
        console.log(`[SimulationManagerService] 재접속 대조: ${outcome.kind}`);
      },
      // 토큰 만료로 거절되면(인증 서버 장애 중 갱신을 못 함) 인증이 새로워질 때까지 기다렸다가 다시 붙는다 — 끝이 아니다
      waitForAuthRenewal,
    );
  };

  try {
    if (!knownId) {
      // 발급: CreateSession 스트림의 첫 프레임에서 id만 받고 닫는다.
      // (발급 스트림 해제~mux attach 사이의 공백은 신규 세션=유휴라
      //  10분 유예·epoch 대체가 덮는다 — 설계서 D1)
      const stream = smClient.createSession({}, { signal });
      for await (const message of stream) {
        if (message.sessionId) {
          knownId = message.sessionId;
          break;
        }
      }
      if (signal.aborted) return;
      if (!knownId) {
        console.error('[SimulationManagerService] No session_id received from server');
        onFatal(new Error('Session stream closed before delivering a session_id'));
        return;
      }
    } else {
      // attach: 실재 프로브 — 죽은 세션에 성공을 돌려주지 않는다.
      await smClient.getSessionState(
        { sessionId: knownId },
        { signal, headers: { 'x-session-id': knownId } },
      );
      if (signal.aborted) return;
    }
  } catch (err) {
    if (!signal.aborted) {
      console.error('[SimulationManagerService] Session establish failed:', err);
      onFatal(err as Error);
    }
    return;
  }

  // 호출자 정착이 mux 기동보다 먼저다 — mux가 즉시 세션 소실을 판정하면
  // onLost가 이 수명을 abort하는데, 그 순간 프라미스가 아직 pending이면
  // 영영 정착하지 않는다(실환경에선 지연에 가려지는 경합).
  setSessionId(knownId);
  useSessionStore.getState().setSessionCreating(false);
  console.log('[SimulationManagerService] Session ready:', knownId);
  onReady(knownId);
  startMux(knownId);
}

/**
 * Start (or resume) the session lifecycle. Resolves with the session id once
 * the liveness stream is established; the lifecycle then keeps running in the
 * background (re-attaching on failover) until {@link closeSession}.
 */
function _startSessionLifecycle(initialSessionId: string | null): Promise<string> {
  _sessionAbortController = new AbortController();
  const abortController = _sessionAbortController;
  useSessionStore.getState().setSessionCreating(true);

  _sessionIdPromise = new Promise<string>((resolve, reject) => {
    let settled = false;
    const firstMessageTimer = setTimeout(() => {
      if (!settled && !abortController.signal.aborted) {
        console.error('[SimulationManagerService] Session establish timed out');
        abortController.abort();
        settled = true;
        reject(new Error('Session timeout: no session_id received'));
      }
    }, SESSION_FIRST_MESSAGE_TIMEOUT_MS);

    runSessionLifecycle(
      abortController,
      initialSessionId,
      (sessionId) => {
        if (settled) return;
        settled = true;
        clearTimeout(firstMessageTimer);
        // 세션 이벤트(제어 평면 종료 통지)는 탭 통합 스트림의 kind로 온다 —
        // 별도 구독 없음 (sessionStreamMux가 applySessionEvent로 라우팅).
        resolve(sessionId);
      },
      (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(firstMessageTimer);
        reject(error);
      },
    ).finally(() => {
      clearTimeout(firstMessageTimer);
      useSessionStore.getState().setSessionCreating(false);
      if (_sessionAbortController === abortController) {
        _sessionAbortController = null;
        _sessionIdPromise = null;
      }
    });
  });

  return _sessionIdPromise;
}

export async function createSession(): Promise<string> {
  const existing = getSessionId();
  if (existing) {
    useSessionStore.getState().setSessionCreating(false);
    console.log('[SimulationManagerService] Session already exists:', existing);
    return existing;
  }
  if (_sessionIdPromise) {
    useSessionStore.getState().setSessionCreating(true);
    console.log('[SimulationManagerService] Session creation already in progress, waiting...');
    return _sessionIdPromise;
  }
  console.log('[SimulationManagerService] Creating session...');
  return _startSessionLifecycle(null);
}

/**
 * Re-attach to an EXISTING session (e.g. restored from storage) and resume the
 * auto-reattaching liveness loop. Most failover recovery happens automatically
 * inside the lifecycle; this is the explicit entry point for resuming a known
 * session id.
 */
export async function attachSession(sessionId: string): Promise<string> {
  const trimmed = sessionId.trim();
  if (!trimmed) {
    throw new Error('attachSession failed: empty session_id provided');
  }
  if (_sessionIdPromise) {
    return _sessionIdPromise;
  }
  console.log('[SimulationManagerService] Attaching to session:', trimmed);
  return _startSessionLifecycle(trimmed);
}

/**
 * 저장된 세션 ID가 있으면 attach를 먼저 시도하고, 죽었으면(NOT_FOUND 등)
 * 폐기 후 새 세션을 만든다 (#286 — 새로고침·순단 후 실행 이어보기의 진입점).
 * attach 실패는 정상 경로다: grace(30초)를 넘겨 회수된 세션이 대부분이므로
 * 조용히 새 세션으로 넘어간다.
 */
export async function attachOrCreateSession(knownSessionId: string | null): Promise<string> {
  const existing = getSessionId();
  if (existing) {
    useSessionStore.getState().setSessionCreating(false);
    return existing;
  }
  if (_sessionIdPromise) {
    useSessionStore.getState().setSessionCreating(true);
    return _sessionIdPromise;
  }
  const trimmed = knownSessionId?.trim();
  if (trimmed) {
    try {
      console.log('[SimulationManagerService] Attaching to stored session:', trimmed);
      return await _startSessionLifecycle(trimmed);
    } catch (err) {
      console.warn(
        '[SimulationManagerService] Stored session attach failed; creating a new session',
        err,
      );
    }
  }
  return _startSessionLifecycle(null);
}

export async function ensureSession(): Promise<string> {
  const existing = getSessionId();
  if (existing) {
    return existing;
  }
  return await createSession();
}

export function closeSession(): void {
  console.log('[SimulationManagerService] Closing session stream (logout)...');
  useSessionStore.getState().setSessionCreating(false);
  stopSessionStreamMux();
  if (_sessionAbortController) {
    _sessionAbortController.abort();
    _sessionAbortController = null;
  }
  _sessionIdPromise = null;
  setSessionId(null);
}

/**
 * 다른 세션으로 갈아탄다 (#288 — 관리 페이지 [이어보기]).
 *
 * 현재 liveness 수명을 끊고(연결만 — 새 수명 정책에서 세션은 서버에서
 * 계속 산다: 무연결 유예 7일) 대상 세션에 attach하는 새 수명을 시작한다.
 * "브라우저당 세션 1개" 구조는 유지 — 활성 세션이 무엇인지가 바뀔 뿐이다.
 */
export async function switchSession(sessionId: string): Promise<string> {
  const trimmed = sessionId.trim();
  if (!trimmed) {
    throw new Error('switchSession failed: empty session_id provided');
  }
  if (getSessionId() === trimmed) {
    return trimmed;
  }
  // 이전 세션의 mux 등록(그 세션의 sim들)이 새 세션 요청에 실리지 않게 정리
  stopSessionStreamMux();
  if (_sessionAbortController) {
    _sessionAbortController.abort();
    // 이전 수명의 finally는 자기 컨트롤러일 때만 전역을 비우므로, 아래
    // _startSessionLifecycle이 덮어쓴 뒤에는 간섭하지 않는다.
    _sessionAbortController = null;
  }
  _sessionIdPromise = null;
  setSessionId(null);
  return _startSessionLifecycle(trimmed);
}

/**
 * 세션 상태 스냅샷 (#288 — 재부착 복원·관리 페이지). BFF 릴레이가 본문
 * session_id로 라우팅 메타데이터를 강제하므로 활성 세션이 아니어도 조회된다.
 */
export async function getSessionState(sessionId: string): Promise<SessionState> {
  return await smClient.getSessionState(new SessionId({ sessionId: sessionId.trim() }));
}

// ============================================
// Builder: CreateSimulation → (실행 폴더에 입력 쓰기) → AddTask × N → Build — 순서는 시작(runStart)이 정한다
// ============================================

/** 실행 기록 헤더(runHistoryHeaders)를 브라우저가 실을 수 있는 꼴로 — Headers 는 ISO-8859-1 바이트만 받으므로 사람 글자인
 *  제목·설명을 퍼센트 인코딩한다(BFF 가 되돌린다). CreateSimulation 과 Build 가 같은 규칙을 쓴다 */
function historyRequestHeaders(headers: Record<string, string>): Headers {
  const safe = { ...headers };
  for (const name of ['x-bff-title', 'x-bff-description']) {
    if (safe[name]) safe[name] = encodeURIComponent(safe[name]);
  }
  return new Headers(safe);
}

/** 실행 id 를 받는다. 실행 기록 헤더를 실으면 BFF 가 그 실행의 기록을 «준비 중» 으로 이때 만든다 — Build 전에 포기된 실행도
 *  SM 의 «빌드되지 않음» 이 그 기록과 실행 폴더를 함께 지운다(docs/DESIGN-run-input-lifecycle.md §6.3) */
export async function createSimulation(headers: Record<string, string> = {}): Promise<string> {
  const simResponse = await smClient.createSimulation(new Empty(), { headers: historyRequestHeaders(headers) });
  const simId = simResponse.simId;
  if (!simId) {
    throw new Error('CreateSimulation failed: empty sim_id returned');
  }
  console.log('[SimulationManagerService] Step 1 - CreateSimulation:', simId);
  return simId;
}

export async function addTask(
  simId: string,
  task: { taskType?: string; args: string[] }
): Promise<string> {
  const request = new AddTaskRequest({
    simId,
    taskType: task.taskType || TASK_TYPE,
    args: task.args,
    startMode: StartMode.AUTO,
  });
  const taskResponse = await smClient.addTask(request);
  if (!taskResponse.taskId) {
    throw new Error(`AddTask failed: empty task_id returned for sim ${simId}`);
  }
  return taskResponse.taskId;
}

export async function build(
  simId: string,
  sharedConfigs: string[] = [],
  headers: Record<string, string> = {}
): Promise<void> {
  try {
    await smClient.build(new BuildRequest({
      simId,
      sharedConfigs,
    }), {
      headers: historyRequestHeaders(headers),
    });
  } catch (err) {
    // Build 의 답을 잃은 것 — 실행이 섰을 수 있어 실패라고 하지 않는다. «답을 잃었을 수 있다» 는 뜻의 정본(`transportMeaning`
    // 'disconnected')이 판정한다: BFF 의 UNAVAILABLE(SM 에 다시 물어도 모를 때 — BFF simulation_manager_server.build, 메시지는
    // 사유만), 게이트웨이의 429·502·503·504(connect 가 Unavailable 로 올린다 — protocol-connect/http-status), 브라우저↔BFF
    // 사이의 단절(fetch 거절 → connect 1.7.0 은 그 TypeError 를 cause 로 한 Unknown — web#684 게이트 리뷰), 시간 안에 답이
    // 오지 않음(DeadlineExceeded — BFF→SM 호출의 기한, 그 사이 SM 이 세웠을 수 있다).
    if (err instanceof ConnectError && transportMeaningOf(err) === 'disconnected') {
      throw new BuildOutcomeUnknownError(simId, err.rawMessage);
    }
    throw err;
  }
  console.log('[SimulationManagerService] Step 3 - Build complete:', simId);
}

/** Build 의 결과를 모른다 — 실행이 섰다면 이력에 «준비 중» 으로 남고, SM 의 종결 저널이 그 행을 닫거나 지운다. message 는 사유다 */
export class BuildOutcomeUnknownError extends Error {
  constructor(readonly simId: string, detail: string) {
    super(detail);
    this.name = 'BuildOutcomeUnknownError';
  }
}

// ============================================
// SimulationManager API
// ============================================

/**
 * List all simulations
 */
export async function listSimulations(): Promise<SimulationInfoList> {
  return await smClient.listSimulations(new Empty());
}

/**
 * List task types
 */
export async function listTaskTypes() {
  return await smClient.listTaskTypes(new Empty());
}

export const isMockMode = () => false;

// ============================================
// Service object export
// ============================================

export const simulationManagerService = {
  createSession,
  attachSession,
  closeSession,
  setSessionId,
  getSessionId,
  ensureSession,
  createSimulation,
  addTask,
  build,
  listSimulations,
  listTaskTypes,
  isMockMode,
};

export default simulationManagerService;
